import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MDText } from 'i18n-react';
import Transition from 'react-transition-group/Transition';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import accepts from 'attr-accept';
import toArray from 'lodash/toArray';

import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import DocumentSelectionModal from '@adsk/bim360-matrix-react-document-selection-modal';
import FileUploadProgress from '@adsk/bim360-matrix-react-file-upload-progress';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';
import uploadFilesIcon from '@adsk/bim360-matrix-react-assets/upload-files.svg';
import alloyUploadFilesIcon from '@adsk/bim360-matrix-react-assets/alloy-upload-files.svg';

import './_FilePicker.scss';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

/**
 * FilePicker
 */
export class FilePicker extends Component {
  /**
   *
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {
      isDocumentSelectionModalOpen: false
    };

    this.closeDocumentSelectionModal = this.closeDocumentSelectionModal.bind(this);
  }

  /**
   * componentWillReceiveProps
   * @param nextProps
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.state.isDocumentSelectionModalOpen &&
      this.props.isSavingDocument &&
      !nextProps.isSavingDocument
    ) {
      this.closeDocumentSelectionModal();
    }
  }

  /**
   * open the input field
   */
  _handleBrowseClick() {
    if (this.props.disabled) {
      return;
    }
    this.props.allowDocsDocuments
      ? this.setState({ isDocumentSelectionModalOpen: true })
      : this.fileInput.click();
  }

  /**
   * convert files from object of object to array of objects
   */
  _handleUploadFiles() {
    if (this.fileInput.files.length > 0) {
      const filesArray = toArray(this.fileInput.files);
      if (!filesArray.find(file => !accepts(file, this.props.acceptFileTypes))) {
        this.props.uploadFiles(Array.prototype.slice.call(this.fileInput.files));
      }
    }
  }

  /**
   * Close document selection modal.
   */
  closeDocumentSelectionModal() {
    this.setState({ isDocumentSelectionModalOpen: false });
  }

  /**
   * Render document selection modal
   * @returns {XML}
   */
  renderDocumentSelectionModal() {
    const { openDocsUrn, isSavingDocument, multiple, ...documentSelectionModalProps } = this.props;

    return (
      <DocumentSelectionModal
        allowUploadMultipleFiles={multiple}
        multi={multiple}
        {...documentSelectionModalProps}
        isModalOpen={this.state.isDocumentSelectionModalOpen}
        closeModal={this.closeDocumentSelectionModal}
        onChoosingDocument={openDocsUrn}
        onChoosingDocuments={openDocsUrn}
        isSaving={isSavingDocument}
      />
    );
  }

  /**
   * Render hidden file input
   * @returns {XML}
   */
  renderHiddenFileInput() {
    return (
      <input
        type="file"
        multiple={this.props.multiple}
        accept={this.props.acceptFileTypes}
        onChange={() => this._handleUploadFiles()}
        style={{ display: 'none' }}
        ref={input => {
          this.fileInput = input;
        }}
      />
    );
  }

  /**
   * Render files upload progress
   */
  renderFiles() {
    const { files, canCancelFiles } = this.props;
    if (!files.length) {
      return null;
    }
    const transitionTimeout = {
      // Set 'enter' timeout to '0' so that enter animation
      // will start immediately.
      enter: 0,

      // Set 'exit' timeout to transition duration + delay so that the 'exited'
      // status won't be applied until animation completes.
      exit: 450
    };
    return (
      <div className="FilePicker__files">
        <TransitionGroup>
          {files.map(file => {
            return (
              <Transition timeout={transitionTimeout} key={file.id}>
                {status => {
                  // Children is a function that receives the current
                  // status of the animation.

                  // Don't render anything if component has 'exited'.
                  if (status === 'exited') {
                    return null;
                  }
                  return (
                    <div className={`FilePicker__file FilePicker__file-animation--${status}`}>
                      <div className="FilePicker__file-spacer" />
                      <FileUploadProgress
                        canCancel={canCancelFiles}
                        onCancel={() => this.props.onFileCancelClick(file)}
                        {...file}
                      />
                    </div>
                  );
                }}
              </Transition>
            );
          })}
        </TransitionGroup>
      </div>
    );
  }

  renderSvgIcon = (isBarStyle, isFlatStyle, theme) => {
    if (isBarStyle || isFlatStyle) {
      return null;
    }
    return theme === Themes.ALLOY ? (
      <SvgIcon className="FilePicker__icon" width="150" height="150">
        {alloyUploadFilesIcon}
      </SvgIcon>
    ) : (
      <SvgIcon className="FilePicker__icon" width="89" height="34">
        {uploadFilesIcon}
      </SvgIcon>
    );
  };

  renderTextOr = isFlatStyle => {
    const textOr = this.props[TSymbolProp].translate('OR');
    if (!isFlatStyle) {
      return <span className="FilePicker__text FilePicker__text-or">{textOr}</span>;
    }
    return null;
  };

  renderTextDrag = isFlatStyle => {
    const textDrag = this.props[TSymbolProp].translate('DRAG_AND_DROP');
    if (!isFlatStyle) {
      return <span className="FilePicker__text FilePicker__text-drag">{textDrag}</span>;
    }
    return null;
  };

  renderTextAttachFiles = isFlatStyle => {
    const textAttachFiles = this.props[TSymbolProp].translate('TO_ATTACH_FILES');

    if (isFlatStyle) {
      return (
        <span className="FilePicker__text FilePicker__text-attach-files">{textAttachFiles}</span>
      );
    }
    return null;
  };

  /**
   * render
   * @returns {XML}
   */
  render() {
    const { allowDocsDocuments, disabled, styleType, files } = this.props;
    const isBarStyle = files.length || styleType === FilePicker.STYLE.BAR;
    const isFlatStyle = styleType === FilePicker.STYLE.FLAT;
    const textBrowse = this.props[TSymbolProp].translate('BROWSE');
    const classes = classNames('FilePicker', `FilePicker--${styleType.toLowerCase()}`, {
      'FilePicker--bar': isBarStyle,
      'FilePicker--flat': isFlatStyle
    });
    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div className={classes}>
            <div className="FilePicker__picker-container">
              {this.renderSvgIcon(isBarStyle, isFlatStyle, theme)}
              <div className="FilePicker__message">
                {this.renderTextDrag(isFlatStyle)}
                <div className="FilePicker__message-browse">
                  {this.renderTextOr(isFlatStyle)}
                  <AnchorButton
                    className={classNames('FilePicker__text FilePicker__text-browse', {
                      'FilePicker__text-browse--flat': isFlatStyle
                    })}
                    onClick={() => this._handleBrowseClick()}
                    disabled={disabled}
                  >
                    {textBrowse}
                  </AnchorButton>
                  {this.renderTextAttachFiles(isFlatStyle)}
                </div>
              </div>
            </div>
            {this.renderFiles()}
            {allowDocsDocuments
              ? this.renderDocumentSelectionModal()
              : this.renderHiddenFileInput()}
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

FilePicker.STYLE = {
  DEFAULT: 'DEFAULT',
  BAR: 'BAR',
  LARGE: 'LARGE',
  FLAT: 'FLAT'
};

FilePicker.defaultProps = {
  files: [],
  styleType: FilePicker.STYLE.DEFAULT
};

FilePicker.propTypes = {
  /**
   * Handles file upload.
   */
  uploadFiles: PropTypes.func.isRequired,
  /**
   * whether to support select of multiple files
   */
  multiple: PropTypes.bool,
  /**
   * Allow to upload files from users computer
   */
  allowUploadFiles: PropTypes.bool,
  /**
   * Accept file types ('.jpeg, .jpg, .png, .pdf' etc.)
   */
  acceptFileTypes: PropTypes.string.isRequired,
  /**
   * Whether or not allow choosing documents from Docs.
   */
  allowDocsDocuments: PropTypes.bool,
  /**
   * Modal container
   */
  container: PropTypes.object,
  /**
   * Selected node ID (only in single selection mode)
   */
  selectedNodeId: PropTypes.string,
  /**
   * Selected node IDs (only in multi selection mode)
   */
  selectedNodesIds: PropTypes.array,
  /**
   * Callback that is called when user selects a document to open.
   * Will be called with selected document URN.
   */
  openDocsUrn: PropTypes.func,
  /**
   * Whether Docs document is being saved.
   */
  isSavingDocument: PropTypes.bool,
  /**
   * Method that returns a promise that gets root project folders from Docs.
   */
  getRootFolders: PropTypes.func,
  /**
   * Method that returns a promise that gets folder's children (both folders & documents).
   */
  getFolderContents: PropTypes.func,
  /**
   * An optional function that returns a boolean indicating whether a folder has more contents (for pagination).
   * The function is of the form:
   * ({ folder, availableChildNodes }) => bool
   *
   * folder is the parent folder object and availableChildNodes contains only the nodes fetched so far for the current folder.
   * This would allow you to determine if a folder has more contents or not based on what the user fetched so far.
   */
  hasMoreFolderContents: PropTypes.func,
  /**
   * In case each folder already contains the data of whether or not it still has more data to load and you don't want implement it using the function above,
   * You can pass this prop to indicate where in each folder the that value exists.
   */
  hasMoreFolderContentsKey: PropTypes.string,
  /**
   * Ensures that only the hasMoreFolderContents function is used to check whether there's more content to fetch (see onLoadMore in Tree.js)
   */
  dontOverrideHasMoreFolderContents: PropTypes.bool,
  /**
   * Locale of the component
   */
  locale: PropTypes.string,
  /**
   * An array of files that are currently being uploaded.
   */
  files: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * File id
       */
      id: PropTypes.string.isRequired,
      /**
       * File name
       */
      name: PropTypes.string.isRequired,
      /**
       * A number between 0 to 100 to indicate file upload progress.
       */
      progress: PropTypes.number,
      /**
       * True to set this file upload progress as failed.
       */
      isFailed: PropTypes.bool
    })
  ),
  /**
   * Set to true if user can cancel uploaded files.
   */
  canCancelFiles: PropTypes.bool,
  /**
   * Callback that is called when user cancels file upload.
   * The callback will be called with the given file as the first parameter.
   */
  onFileCancelClick: PropTypes.func,
  /**
   * Whether or not the FilePicker is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The kind of style for the file picker. Can be one of FilePicker.STYLE.
   */
  styleType: PropTypes.oneOf(objectValues(FilePicker.STYLE)),
  /**
   * Disabled node IDs (only in multi selection mode)
   */
  disabledNodesIds: PropTypes.array
};

export default withLocale(FilePicker, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
